import { formatDate } from "../../../../utils/DateUtils";

// Rótulo do alvo da regra: o que ela comissiona.
// O nome do produto/categoria vem pronto do backend (scopeName), porque o scopeId sozinho é
// um id cru cujo significado depende do scopeType.
const scopeRenderer = (params) => {
	const scopeType = params.data?.scopeType;
	const scopeName = params.data?.scopeName;

	if (scopeType === 'ALL') {
		return 'Todos os produtos e serviços';
	}

	// No escopo por tipo o próprio scopeName já vem como rótulo pronto ("Todos os produtos"),
	// então prefixar com "Tipo:" só repetiria a informação.
	if (scopeType === 'PRODUCT_TYPE') {
		return scopeName || 'Por tipo';
	}

	// "Produto" sozinho esconderia que serviços entram aqui também: no modelo os dois são
	// a mesma entidade, e a maior parte das regras é justamente sobre serviço.
	const prefix = scopeType === 'PRODUCT' ? 'Produto/Serviço' : 'Categoria';
	// Alvo apagado depois da regra criada: mostra o tipo em vez de quebrar a linha.
	return scopeName ? `${prefix}: ${scopeName}` : `${prefix} removido`;
};

// Colaborador em branco significa que a regra vale para todo mundo, e isso precisa aparecer
// escrito: célula vazia se confunde com dado faltando.
const employeeRenderer = (params) => params.data?.employee?.fullName || 'Todos os colaboradores';

const branchRenderer = (params) => params.data?.branch?.name || 'Todas as filiais';

const percentRenderer = (params) => {
	// Regra escalonada não tem um percentual só, tem uma tabela. Mostrar o intervalo
	// (menor a maior faixa) na listagem evita abrir o cadastro para saber quanto ela paga.
	if (params.data?.paymentType === 'TIERED') {
		const percents = (params.data?.tiers || []).map(tier => tier.percent).filter(p => p != null);
		if (percents.length === 0) {
			return 'Por faixa';
		}
		const menor = Math.min(...percents).toString().replace('.', ',');
		const maior = Math.max(...percents).toString().replace('.', ',');
		return menor === maior ? `${menor}% (faixa)` : `${menor}% a ${maior}% (faixa)`;
	}

	const percent = params.data?.percent;
	return percent != null ? `${percent.toString().replace('.', ',')}%` : '-';
};

// A vigência é o que faz o histórico não ser reescrito, então precisa estar legível na
// listagem: sem data final, a regra é a que está valendo daqui para frente.
const validityRenderer = (params) => {
	const from = params.data?.validFrom;
	const to = params.data?.validTo;

	if (!from) {
		return '-';
	}

	const start = formatDate(from);
	return to ? `${start} até ${formatDate(to)}` : `Desde ${start}`;
};

// Diferencia o que está valendo hoje do que é histórico ou programado para o futuro. Sem isso
// o operador não sabe qual das linhas está de fato comissionando as vendas de hoje.
//
// Usa a família translúcida do tema (`light badge-*`), e não a sólida: só ela tem regra para
// o tema escuro. A sólida pinta o fundo e mantém o texto branco em qualquer tema, e o
// `badge-light` de "Encerrada" era fundo #eee fixo, uma mancha branca no escuro. Na variante
// translúcida a cor da marca fica no texto e só o fundo muda entre os temas.
const currentRenderer = (params) => {
	const isCurrent = params.data?.current === true;
	const from = params.data?.validFrom;
	const isFuture = from && new Date(from) > new Date();

	if (isCurrent) {
		return <span className="badge light badge-success">Vigente</span>;
	}
	if (isFuture) {
		return <span className="badge light badge-info">Programada</span>;
	}
	return <span className="badge light badge-dark">Encerrada</span>;
};

// Colunas do grid.
export const Columns = [
	{
		headerName: 'scope',
		field: 'scopeName',
		width: 260,
		cellRenderer: scopeRenderer,
		// scopeName é resolvido no controller e não é atributo da entidade, então ordenar
		// por ele quebraria a consulta.
		sortable: false
	},
	{
		headerName: 'employee',
		field: 'employee.fullName',
		width: 200,
		cellRenderer: employeeRenderer,
		sortable: false
	},
	{
		headerName: 'commission',
		field: 'percent',
		width: 120,
		cellRenderer: percentRenderer
	},
	{
		headerName: 'validity',
		field: 'validFrom',
		width: 220,
		cellRenderer: validityRenderer
	},
	{
		headerName: 'branch',
		field: 'branch.name',
		width: 170,
		cellRenderer: branchRenderer,
		sortable: false
	},
	{
		headerName: 'situation',
		field: 'current',
		width: 130,
		cellRenderer: currentRenderer,
		sortable: false
	},
	{
		headerName: 'status',
		field: 'status',
		type: 'trans',
		width: 120
	}
]
